import type { CoasterFamilyDefinition } from '../physics/index.ts';
import type { CoasterProject } from '../schema/index.ts';

export interface CoasterFamilyPresentation {
  readonly id: string;
  readonly label: string;
  readonly trackStyleId: string;
  readonly trainRigId: string;
  readonly supportStyleId: string;
  readonly audioProfileId: string;
  readonly cameraAnchorSetId: string;
  readonly supportedSections: ReadonlySet<string>;
  readonly clearance: CoasterFamilyClearanceEnvelope;
}

export interface CoasterFamilyClearanceEnvelope {
  readonly version: string;
  readonly lateralHalfWidthM: number;
  readonly belowTrackM: number;
  readonly aboveTrackM: number;
  readonly maximumSampleSpacingM: number;
  readonly maximumFrameAngleDegrees: number;
  readonly advisoryMarginM: number;
}

export const STANDARD_STEEL_CLEARANCE: CoasterFamilyClearanceEnvelope = Object.freeze({
  version: 'steel.standard.clearance.v1',
  lateralHalfWidthM: 0.95,
  belowTrackM: 0.35,
  aboveTrackM: 2.2,
  maximumSampleSpacingM: 0.5,
  maximumFrameAngleDegrees: 5,
  advisoryMarginM: 0.5,
});

const STANDARD_STEEL: CoasterFamilyPresentation = {
  id: 'steel.standard',
  label: 'Standard Steel',
  trackStyleId: 'steel.double-rail.v1',
  trainRigId: 'steel.sitdown.procedural-v1',
  supportStyleId: 'steel.tubular.auto-v1',
  audioProfileId: 'steel.wheel-wind.procedural-v1',
  cameraAnchorSetId: 'steel.sitdown.anchors-v1',
  supportedSections: new Set(['station', 'normal', 'lift', 'launch', 'brake', 'photo']),
  clearance: STANDARD_STEEL_CLEARANCE,
};

const REGISTRY = new Map([[STANDARD_STEEL.id, STANDARD_STEEL]]);

export function coasterFamilyPresentation(familyId: string): CoasterFamilyPresentation {
  return REGISTRY.get(familyId) ?? STANDARD_STEEL;
}

export function coasterFamilyIds(): readonly string[] {
  return [...REGISTRY.keys()];
}

export function buildFamilyPhysicsDefinition(project: CoasterProject): CoasterFamilyDefinition {
  const presentation = coasterFamilyPresentation(project.coasterFamilyId);
  return {
    id: presentation.id,
    capabilities: presentation.supportedSections,
    limits: { maxSpeed: 100, maxCars: 12 },
    train: {
      carCount: project.train.carCount,
      carMass: project.train.emptyMassKg / project.train.carCount,
      carSpacing: project.train.carSpacingMeters,
      bogieSpacing: 1.25,
      rollingResistanceCoefficient: 0.012,
      dragCoefficient: 0.9,
      dragArea: 1.7,
    },
  };
}
